/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
/**
 * NoOpDismissContext - Lets the sibling StickyFooter register a one-click
 * dismiss handler that the "Already up to date" EmptyState card can invoke,
 * without duplicating the footer's reject/freeze/navigation logic.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

interface NoOpDismissRegistration {
  /** One-click dismiss handler (footer's reject/dismiss path). */
  dismiss: () => void;
  /** True while a dismiss request is in flight (footer's submitting state). */
  isDismissing: boolean;
}

interface NoOpDismissContextValue {
  /**
   * Producer (StickyFooter) registers its dismiss handler + in-flight flag when
   * the proposal is fully no-op & pending. Pass `null` to unregister.
   */
  register: (registration: NoOpDismissRegistration | null) => void;
  /** Consumer (card) triggers the registered dismiss. No-op if none registered. */
  dismiss: () => void;
  /** Mirrors the registered handler's in-flight flag; false when none registered. */
  isDismissing: boolean;
  /** True iff a dismiss handler is currently registered (footer is in no-op mode). */
  canDismiss: boolean;
}

const NoOpDismissContext = createContext<NoOpDismissContextValue | null>(null);

export function NoOpDismissProvider({ children }: { children: ReactNode }) {
  const handlerRef = useRef<(() => void) | null>(null);
  const [canDismiss, setCanDismiss] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  // Empty-dep useCallback => STABLE identity forever. The producer's register
  // effect depends on this stable ref, so provider setState below never
  // re-triggers that effect (no loop).
  const register = useCallback(
    (registration: NoOpDismissRegistration | null) => {
      handlerRef.current = registration?.dismiss ?? null;
      setCanDismiss(registration != null);
      setIsDismissing(registration?.isDismissing ?? false);
    },
    []
  );
  const dismiss = useCallback(() => {
    handlerRef.current?.();
  }, []);

  const value = useMemo(
    () => ({ register, dismiss, isDismissing, canDismiss }),
    [register, dismiss, isDismissing, canDismiss]
  );

  return <NoOpDismissContext value={value}>{children}</NoOpDismissContext>;
}

/**
 * Optional version that returns null when outside a provider.
 * Useful for components that work with or without a dismiss registration.
 */
export function useNoOpDismissOptional(): NoOpDismissContextValue | null {
  return useContext(NoOpDismissContext);
}
